import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@meno/ui/components/ui/dialog'
import { Button } from '@meno/ui/components/ui/button'
import {
  Download, ExternalLink, ZoomIn, ZoomOut, RotateCcw,
  FileText, Link2 as LinkIcon, Music, Video, Image as ImageIcon,
} from 'lucide-react'
import { Badge } from '@meno/ui/components/ui/badge'
import { BACKEND_URL } from '@meno/ui/lib/api'
import { formatBytes } from '@meno/ui/lib/utils'
import type { MediaFile, AudioFile } from '@/pages/management/media-page/utils'
import type { InteractiveLink } from '@/components/CreateLinkDialog'
import PdfThumbnail from '@/components/PdfThumbnail'

// Preview target — pick exactly one kind, then pass the matching payload.
// The dialog renders the controls appropriate to the kind:
//   image → zoomable image
//   video → <video controls>
//   audio → <audio controls>
//   pdf   → in-frame PDF viewer
//   link  → "open in new tab" CTA (we can't iframe arbitrary URLs reliably)
export type PreviewAsset =
  | { kind: 'media'; file: MediaFile }
  | { kind: 'audio'; file: AudioFile }
  | { kind: 'link';  link: InteractiveLink }

type Props = {
  asset: PreviewAsset | null
  onClose: () => void
}

export default function AssetPreviewDialog({ asset, onClose }: Props) {
  const { t } = useTranslation()
  // Image zoom is local to a single preview session. Reset to 1 when the
  // dialog re-opens with a different asset (the key= on Dialog handles it).
  const [zoom, setZoom] = useState(1)

  if (!asset) {
    return (
      <Dialog open={false} onOpenChange={() => {}}>
        <DialogContent />
      </Dialog>
    )
  }

  const resolve = (url: string) => url.startsWith('http') ? url : `${BACKEND_URL}${url}`

  let title = ''
  let subtitle = ''
  let body: React.ReactNode = null
  let resolvedUrl: string | null = null
  let kindIcon: React.ReactNode = null
  let kindLabel = ''
  let downloadable = false

  if (asset.kind === 'media') {
    const f = asset.file
    resolvedUrl = resolve(f.url)
    title = f.original_name
    subtitle = f.file_size ? formatBytes(f.file_size) : ''
    downloadable = true
    const isImage = f.file_type.startsWith('image')
    const isVideo = f.file_type.startsWith('video')
    const isPdf   = f.file_type === 'application/pdf' || /\.pdf$/i.test(f.original_name)
    if (isImage) {
      kindIcon = <ImageIcon className="w-3 h-3" />
      kindLabel = t('common.image', 'Image')
      body = (
        <div className="relative flex items-center justify-center bg-muted/40 rounded-lg overflow-auto max-h-[70vh]">
          <img
            src={resolvedUrl}
            alt={f.original_name}
            className="max-w-none transition-transform"
            style={{ transform: `scale(${zoom})`, transformOrigin: 'center' }}
          />
        </div>
      )
    } else if (isVideo) {
      kindIcon = <Video className="w-3 h-3" />
      kindLabel = t('common.video', 'Video')
      body = (
        <div className="bg-black rounded-lg overflow-hidden flex items-center justify-center max-h-[70vh]">
          <video
            src={resolvedUrl}
            controls
            autoPlay
            playsInline
            className="w-full max-h-[70vh]"
          />
        </div>
      )
    } else if (isPdf) {
      kindIcon = <FileText className="w-3 h-3" />
      kindLabel = t('common.document', 'Document')
      // Canvas-rendered first-page thumbnail on top; native PDF viewer below
      // for scrolling through the rest of the document. Two views in one panel
      // because operators reach this dialog both to "what's in this file?"
      // (the thumbnail answers in <100ms even when the browser PDF viewer is
      // slow to wake) and to actually read the document.
      body = (
        <div className="grid gap-3">
          <PdfThumbnail
            url={resolvedUrl}
            targetWidth={800}
            fit="contain"
            className="w-full max-h-[35vh] rounded-lg border border-border"
          />
          <iframe
            src={resolvedUrl}
            title={f.original_name}
            className="w-full h-[40vh] rounded-lg border border-border bg-muted/40"
          />
        </div>
      )
    } else {
      kindIcon = <FileText className="w-3 h-3" />
      kindLabel = t('common.file', 'File')
      body = (
        <div className="flex flex-col items-center justify-center gap-3 py-16 bg-muted/40 rounded-lg">
          <FileText className="w-12 h-12 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {t('assets.previewUnsupported', 'No inline preview available for this file type.')}
          </p>
        </div>
      )
    }
  } else if (asset.kind === 'audio') {
    const f = asset.file
    resolvedUrl = resolve(f.url)
    title = f.original_name
    subtitle = f.file_size ? formatBytes(f.file_size) : ''
    downloadable = true
    kindIcon = <Music className="w-3 h-3" />
    kindLabel = t('common.audio', 'Audio')
    body = (
      <div className="flex flex-col items-center justify-center gap-6 py-12 bg-pink-500/10 rounded-lg">
        <div className="w-24 h-24 rounded-3xl bg-pink-500 flex items-center justify-center shadow-lg">
          <Music className="w-12 h-12 text-white" />
        </div>
        <audio src={resolvedUrl} controls autoPlay className="w-full max-w-md" />
      </div>
    )
  } else {
    const l = asset.link
    title = l.name
    subtitle = l.interactive_link
    resolvedUrl = l.interactive_link
    kindIcon = <LinkIcon className="w-3 h-3" />
    kindLabel = t('common.link', 'Webpage')
    let host = l.interactive_link
    try { host = new URL(l.interactive_link).hostname.replace(/^www\./, '') } catch {}
    body = (
      <div className="flex flex-col items-center justify-center gap-4 py-16 bg-emerald-500/10 rounded-lg">
        <div className="w-20 h-20 rounded-2xl bg-emerald-500 flex items-center justify-center shadow-md">
          <LinkIcon className="w-10 h-10 text-white" />
        </div>
        <div className="text-center">
          <p className="text-sm font-medium text-foreground">{host}</p>
          <p className="text-xs text-muted-foreground mt-1 break-all max-w-md">{l.interactive_link}</p>
        </div>
      </div>
    )
  }

  const isImage = asset.kind === 'media' && asset.file.file_type.startsWith('image')

  return (
    <Dialog open={!!asset} onOpenChange={o => { if (!o) onClose() }}>
      <DialogContent className="max-w-4xl w-[92vw] p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pe-8 min-w-0">
            <Badge variant="secondary" className="text-[10px] gap-1 shrink-0">
              {kindIcon}
              {kindLabel}
            </Badge>
            <span className="truncate text-base font-semibold">{title}</span>
          </DialogTitle>
          {subtitle && (
            <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
          )}
        </DialogHeader>

        {body}

        {/* Action row — image zoom controls on the start, open / download on
            the end. Audio + video already render native controls, so this
            row only ever offers tertiary actions. */}
        <div className="flex items-center justify-between gap-2 pt-2">
          <div className="flex items-center gap-1">
            {isImage && (
              <>
                <Button
                  size="sm" variant="outline" className="gap-1.5 h-8"
                  onClick={() => setZoom(z => Math.max(0.25, z - 0.25))}
                  disabled={zoom <= 0.25}
                >
                  <ZoomOut className="w-3.5 h-3.5" />
                </Button>
                <Button
                  size="sm" variant="outline" className="gap-1.5 h-8"
                  onClick={() => setZoom(z => Math.min(4, z + 0.25))}
                  disabled={zoom >= 4}
                >
                  <ZoomIn className="w-3.5 h-3.5" />
                </Button>
                <Button
                  size="sm" variant="ghost" className="gap-1.5 h-8"
                  onClick={() => setZoom(1)}
                  disabled={zoom === 1}
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span className="text-xs tabular-nums">{Math.round(zoom * 100)}%</span>
                </Button>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {resolvedUrl && (
              <Button size="sm" variant="outline" className="gap-1.5 h-8" asChild>
                <a href={resolvedUrl} target="_blank" rel="noreferrer">
                  <ExternalLink className="w-3.5 h-3.5" />
                  {t('common.openInNewTab', 'Open')}
                </a>
              </Button>
            )}
            {downloadable && resolvedUrl && (
              <Button size="sm" className="gap-1.5 h-8" asChild>
                <a href={resolvedUrl} download={title}>
                  <Download className="w-3.5 h-3.5" />
                  {t('common.download', 'Download')}
                </a>
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
